"use client";

import type { Filter } from "@orsane/schema/filters";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { produce } from "immer";
import { ListFilterPlus } from "lucide-react";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import FilterPartComponent from "./FilterPartComponent";

export default function TaskFilter() {
    const { filter } = useSearch({ strict: false }) as { filter: Filter[] };
    const [filterParts, setFilterParts] = useState<Filter[]>(filter || []);
    const [activeFilterId, setActiveFilterId] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        setFilterParts(filter || []);
    }, [filter]);

    const addFilterPart = () => {
        const newFilter: Filter = {
            id: nanoid(5),
            type: null,
            operator: null,
            value: null,
        };
        setFilterParts([...filterParts, newFilter]);
        setActiveFilterId(newFilter.id);
    };

    const removeFilterPart = (id: string) => {
        const newParts = produce(filterParts, (draft) => {
            return draft.filter((part) => part.id !== id);
        });
        setFilterParts(newParts);
        if (activeFilterId === id) {
            setActiveFilterId(null);
        }
        persistFilterInUrl(newParts);
    };

    const handleUpdateType = (id: string, newType: Filter["type"]) => {
        const nextParts = produce(filterParts, (draft: Filter[]) => {
            const idx = draft.findIndex((part: Filter) => part.id === id);
            if (idx !== -1) {
                draft[idx].type = newType;
            }
        });
        setFilterParts(nextParts);

        persistFilterInUrl(nextParts);
    };

    const handleUpdateOperator = (id: string, newOperator: Filter["operator"]) => {
        const nextParts = produce(filterParts, (draft: Filter[]) => {
            const idx = draft.findIndex((part: Filter) => part.id === id);
            if (idx !== -1) {
                draft[idx].operator = newOperator;
            }
        });
        setFilterParts(nextParts);

        persistFilterInUrl(nextParts);
    };

    const handleUpdateValue = (id: string, newValue: any) => {
        const nextParts = produce(filterParts, (draft: Filter[]) => {
            const idx = draft.findIndex((part: Filter) => part.id === id);
            if (idx !== -1) {
                draft[idx].value = newValue;
            }
        });
        setFilterParts(nextParts);

        persistFilterInUrl(nextParts);
    };

    const persistFilterInUrl = (newParts: Filter[]) => {
        // Check every part has a type, operator and value
        const hasType = newParts.every((part) => !!part.type);
        const hasOperator = newParts.every((part) => !!part.operator);
        const hasValue = newParts.every((part) => !!part.value);

        if (!hasType || !hasOperator || !hasValue) {
            return;
        }

        navigate({
            to: ".",
            search: {
                filter: newParts.length > 0 ? newParts : undefined,
            },
        });
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-row flex-wrap items-center gap-2">
                {filterParts.length > 0 &&
                    filterParts.map((part) => (
                        <FilterPartComponent
                            key={part.id}
                            filter={part}
                            isActive={activeFilterId === part.id}
                            onActivate={() => setActiveFilterId(part.id)}
                            onUpdateType={(value) => handleUpdateType(part.id, value as Filter["type"])}
                            onUpdateOperator={(value) => handleUpdateOperator(part.id, value as Filter["operator"])}
                            onUpdateValue={(value) => handleUpdateValue(part.id, value)}
                            onRemove={() => removeFilterPart(part.id)}
                        />
                    ))}

                <Tooltip>
                    <TooltipTrigger
                        render={
                            <Button
                                className="size-7"
                                variant="ghost"
                                size="icon"
                                onClick={addFilterPart}
                                title="Add filter"
                            >
                                <ListFilterPlus className="size-4" />
                                <span className="sr-only">Add filter</span>
                            </Button>
                        }
                    ></TooltipTrigger>
                    <TooltipContent>Add filter</TooltipContent>
                    <div id="filter-loader-portal" />
                </Tooltip>
            </div>
        </div>
    );
}
